import React, { useMemo, useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useScrollLock } from '../../_utils/useScrollLock';
import useGiftCardShopData from './useGiftCardShopData';
import GiftCardPreview from './GiftCardPreview';
import StepProduct from './StepProduct';
import StepAmount from './StepAmount';
import StepDesign from './StepDesign';
import StepPersonalise from './StepPersonalise';
import StepBuyer from './StepBuyer';
import StepSummary from './StepSummary';
import AuthPromptModal from '../newSalon/SalonProfilePage/AuthPromptModal';
import { resolveDesignsForVoucher, defaultAmountFor, payFor } from './shopUtils';
import { giftCardService } from '../../_services/giftCard.service';
import { userService } from '../../_services/user.service';
import './GiftCardShop.css';

// Draft persisted across the login round-trip (mirrors the booking flow's
// `pendingBooking`). The customer fills the early steps logged-out, signs in at
// the details step, and lands back here with their progress intact.
const DRAFT_KEY = 'pendingGiftCard';

// The logged-in client, if any (the booking flow stores userInfo under 'user').
const readAccount = () => {
  try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch (e) { return null; }
};
const hasToken = () => !!localStorage.getItem('accessToken');

// Icons match the app's inline-SVG convention (SalonProfilePage / booking modals).
const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </svg>
);
const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const isEmail = (s) => /\S+@\S+\.\S+/.test(s || '');

const DEFAULT_PERSONALISE = {
  giftMode: 'self',
  recipientName: '', recipientEmail: '', message: '', fromName: '',
  deliverWhen: 'now', deliverDate: '', deliverEmail: true, deliverPdf: false,
  buyerName: '', buyerEmail: '',
};

const STEP_HEADING = {
  product: 'Choose a gift card',
  amount: 'Choose an amount',
  design: 'Choose a design',
  details: 'Sending & details',
  pay: 'Review & pay',
};

// GiftCardShopModal — the customer gift-card purchase flow as a modal: a
// slide-up sheet on mobile, a centred two-pane showcase on desktop (live card
// preview pinned beside the current step). Drives a 5-step wizard.
const GiftCardShopModal = ({ salonIdOrSlug, salonSlug, onClose }) => {
  const { loading, salonName, currency, vouchers, designs } = useGiftCardShopData(salonIdOrSlug);

  // Order state
  const [selectedVoucherId, setSelectedVoucherId] = useState(null);
  const [amount, setAmount] = useState(50);
  const [selectedDesignId, setSelectedDesignId] = useState(null);
  const [personalise, setPersonalise] = useState(DEFAULT_PERSONALISE);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(null);
  const updp = (patch) => setPersonalise(p => ({ ...p, ...patch }));

  // Auth — the buyer signs in / creates an account at the details step (the
  // payment endpoint is authed and the gift card is tied to their account).
  // Read once at mount; the component remounts on return from the login flow.
  const account = useMemo(readAccount, []);
  const loggedIn = useMemo(hasToken, []);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [seeded, setSeeded] = useState(false);   // guard: seed/restore runs once
  const [jumpToDetails, setJumpToDetails] = useState(false);

  // Wizard state
  const [stepIndex, setStepIndex] = useState(0);
  const [dir, setDir] = useState(1);       // 1 = forward, -1 = back (drives transition)
  const [closing, setClosing] = useState(false);
  const [previewCollapsed, setPreviewCollapsed] = useState(false);
  const previewBoxRef = useRef(null);
  const stepAreaRef = useRef(null);
  const collapseLockUntil = useRef(0); // ignore clamp-induced scrolls just after collapsing
  const lastScrollY = useRef(0);       // for detecting scroll direction
  const [previewH, setPreviewH] = useState(null); // natural card height, drives the pane height

  // Shared ref-counted lock: overlapping modals must not restore each
  // other's locked styles (see SalonProfilePage/ConfirmBookingModal).
  useScrollLock(true);

  // Seed defaults — or restore a draft saved before a sign-in round-trip —
  // once the catalogue arrives. Runs once (the `seeded` guard).
  useEffect(() => {
    if (!vouchers.length || seeded) return;

    // Restore a persisted draft if it's for this page and we're now signed in.
    let draft = null;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d && d.returnUrl === window.location.pathname) draft = d;
      }
    } catch (e) { /* ignore malformed draft */ }

    if (draft && hasToken()) {
      localStorage.removeItem(DRAFT_KEY);
      if (vouchers.some(v => v.id === draft.selectedVoucherId)) {
        setSelectedVoucherId(draft.selectedVoucherId);
      } else {
        setSelectedVoucherId(vouchers[0].id);
      }
      if (typeof draft.amount === 'number') setAmount(draft.amount);
      if (draft.selectedDesignId) setSelectedDesignId(draft.selectedDesignId);
      if (draft.personalise) setPersonalise(p => ({ ...p, ...draft.personalise }));
      setJumpToDetails(true);   // land them back on the details step, signed in
      setSeeded(true);
      return;
    }

    // A shared deep link (?voucher=ID) pre-selects that voucher; otherwise default
    // to the first one.
    const deepVoucherId = new URLSearchParams(window.location.search).get('voucher');
    const v = (deepVoucherId && vouchers.find((x) => String(x.id) === String(deepVoucherId))) || vouchers[0];
    setSelectedVoucherId(v.id);
    setAmount(defaultAmountFor(v));
    // Leave the design unselected so the preview shows the voucher's title card
    // (cover) until the customer explicitly picks one in the Design step.
    setSelectedDesignId(null);
    setSeeded(true);
  }, [vouchers, designs, seeded]);

  // Prefill the buyer's name/email from their account once signed in, so the
  // details step doesn't ask for what we already know. Only fills blanks.
  useEffect(() => {
    if (!loggedIn || !account) return;
    setPersonalise(p => ({
      ...p,
      buyerName: p.buyerName || account.name || account.fullName || '',
      buyerEmail: p.buyerEmail || account.email || '',
    }));
  }, [loggedIn, account]);

  // The localStorage account object doesn't always carry an email (it varies
  // by which flow signed the user in) — without one the details step could
  // never validate. Fall back to the profile API; if that fails too, the
  // buyer fields render editable instead of locked, so it's never a dead end.
  useEffect(() => {
    if (!loggedIn || isEmail(personalise.buyerEmail)) return;
    let alive = true;
    userService.getClientProfile()
      .then((res) => {
        const rd = res?.data?.responseData || res?.data || {};
        const u = rd.userInfo || rd.user || rd;
        if (!alive || !u) return;
        setPersonalise(p => ({
          ...p,
          buyerName: p.buyerName || u.name || [u.first_name, u.last_name].filter(Boolean).join(' ') || '',
          buyerEmail: p.buyerEmail || u.email || '',
        }));
      })
      .catch(() => { /* fields stay editable */ });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn]);

  const voucher = useMemo(
    () => vouchers.find(v => v.id === selectedVoucherId) || vouchers[0] || null,
    [vouchers, selectedVoucherId]
  );
  const voucherDesigns = useMemo(() => {
    const list = resolveDesignsForVoucher(voucher, designs);
    // Lead the gallery with the voucher's title card (the cover the salon
    // chose) so the customer's first option is the featured design.
    const coverId = voucher && voucher.coverDesignId;
    const idx = coverId ? list.findIndex(d => d.id === coverId) : -1;
    if (idx <= 0) return list;
    const copy = list.slice();
    copy.unshift(copy.splice(idx, 1)[0]);
    return copy;
  }, [voucher, designs]);
  const selectedDesign = useMemo(() => {
    // The customer's explicit pick (from the Design step) always wins.
    const picked = selectedDesignId && voucherDesigns.find(d => d.id === selectedDesignId);
    if (picked) return picked;
    // Otherwise show the voucher's "title card" — the cover the salon chose —
    // so each voucher previews as its own design, falling back to the first
    // offered design when no cover is set.
    const cover = voucher && voucher.coverDesignId
      ? voucherDesigns.find(d => d.id === voucher.coverDesignId)
      : null;
    return cover || voucherDesigns[0] || null;
  }, [voucher, voucherDesigns, selectedDesignId]);
  const isGift = personalise.giftMode === 'gift';

  // Steps — each "choose" step only appears when there's an actual choice:
  //  - product: only when there's more than one gift card for sale
  //  - amount: only with multiple denominations or a custom amount
  //  - design: only when more than one design is offered
  // so the customer never sees a single-option page they can only click through.
  const steps = useMemo(() => {
    const s = [];
    if (vouchers.length > 1) s.push('product');
    const denoms = (voucher && voucher.denominations) || [];
    const hasAmountChoice = !!(voucher && voucher.customAmount) || denoms.length > 1;
    if (hasAmountChoice) s.push('amount');
    if (voucherDesigns.length > 1) s.push('design');
    s.push('details', 'pay');
    return s;
  }, [vouchers.length, voucher, voucherDesigns.length]);
  const currentStep = steps[stepIndex] || steps[0];

  // After restoring a draft, jump to the details step once the step set is known.
  useEffect(() => {
    if (!jumpToDetails || !steps.length) return;
    const i = steps.indexOf('details');
    if (i >= 0) { setDir(1); setStepIndex(i); }
    setJumpToDetails(false);
  }, [jumpToDetails, steps]);

  // Reset the shrunk preview (and scroll to top) whenever the step changes.
  useEffect(() => {
    setPreviewCollapsed(false);
    lastScrollY.current = 0;
    if (stepAreaRef.current) stepAreaRef.current.scrollTop = 0;
  }, [stepIndex]);

  // Measure the card's natural (unscaled) height so the preview pane can reclaim
  // exactly the space the scaled card frees. offsetHeight ignores the transform,
  // so this stays accurate whether or not the preview is currently collapsed.
  // Runs in a layout effect so the expanded height is a concrete pixel value
  // before paint — height transitions stall if they start from `auto`.
  useLayoutEffect(() => {
    const inner = previewBoxRef.current && previewBoxRef.current.querySelector('.gcs-preview');
    if (inner) setPreviewH(inner.offsetHeight);
  }, [currentStep, selectedDesign, amount, isGift, personalise.recipientName, personalise.message, personalise.fromName]);

  // On any step whose content scrolls, the preview recedes as you scroll down to
  // free room, and springs back to full size the moment you scroll up. We only
  // collapse when there's enough overflow to still scroll afterwards — otherwise
  // shrinking would remove the overflow, clamp scrollTop to 0 and bounce the card
  // open. A short lock after collapsing ignores those clamp-induced scrolls.
  const handleStepScroll = (e) => {
    // Desktop (two-pane) renders the preview as a static side pane — no
    // scroll-shrink there. (Collapsing would also flick the card's drop-shadow
    // off and on, which reads as a jarring shadow behind the card.) Matches the
    // two-pane breakpoint in the CSS.
    if (typeof window !== 'undefined' && window.matchMedia('(min-width: 900px)').matches) return;
    const el = e.currentTarget;
    const y = el.scrollTop;
    const dy = y - lastScrollY.current;
    lastScrollY.current = y;
    setPreviewCollapsed((prev) => {
      if (prev) {
        if (Date.now() < collapseLockUntil.current) return true;
        // Scrolling up (or reaching the top) brings the card back to full size.
        if (y <= 6 || dy < -4) return false;
        return true;
      }
      // Scrolling down, with room to keep scrolling afterwards, shrinks it.
      if (dy > 0 && y > 40 && (el.scrollHeight - el.clientHeight) > 150) {
        collapseLockUntil.current = Date.now() + 650;
        return true;
      }
      return false;
    });
  };

  const handleClose = () => {
    if (closing) return;
    setClosing(true);
    setTimeout(() => onClose && onClose(), 300);
  };
  // Save the in-progress order so the sign-in round-trip doesn't lose it, then
  // open the auth prompt. On return to this route the seed/restore effect picks
  // the draft back up and drops the buyer on the details step, signed in.
  const persistDraftAndPromptAuth = () => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        returnUrl: window.location.pathname,
        selectedVoucherId, amount, selectedDesignId, personalise,
      }));
    } catch (e) { /* storage full / unavailable — proceed to prompt anyway */ }
    setShowAuthPrompt(true);
  };

  const goNext = () => {
    if (stepIndex >= steps.length - 1) return;
    // Gate at the details step: you must be signed in before personalising,
    // so the buyer's identity comes from their account (same as booking).
    if (steps[stepIndex + 1] === 'details' && !loggedIn) {
      persistDraftAndPromptAuth();
      return;
    }
    setDir(1); setStepIndex(i => i + 1);
  };
  const goBack = () => {
    if (stepIndex > 0) { setDir(-1); setStepIndex(i => i - 1); } else handleClose();
  };

  const handleSelectVoucher = (id) => {
    const v = vouchers.find(x => x.id === id);
    setSelectedVoucherId(id);
    setAmount(defaultAmountFor(v));
    // Reset to the new voucher's title card (cleared selection → cover shows).
    setSelectedDesignId(null);
  };

  const detailsValid =
    isEmail(personalise.buyerEmail) &&
    (personalise.deliverEmail || personalise.deliverPdf) &&
    (!isGift || isEmail(personalise.recipientEmail)) &&
    !(isGift && personalise.deliverWhen === 'scheduled' && !personalise.deliverDate);

  const stepValid = (() => {
    switch (currentStep) {
      case 'product': return !!voucher;
      case 'amount': return amount > 0;
      case 'design': return !!selectedDesign;
      case 'details': return detailsValid;
      default: return true;
    }
  })();

  const handlePay = async () => {
    if (!detailsValid || paying) return;

    // Authenticated buyers only (no guest) — gift-card purchase mirrors the
    // booking flow. The details-step gate normally catches this; this is the
    // belt-and-braces fallback if somehow reached signed-out (persist + prompt
    // so progress survives the round-trip).
    if (!loggedIn) {
      persistDraftAndPromptAuth();
      return;
    }

    const base = salonSlug ? `/book/${salonSlug}` : `/salon/${salonIdOrSlug}`;
    const order = {
      salonId: salonIdOrSlug,
      voucherId: voucher && voucher.id,
      amount,
      pay: payFor(voucher, amount),
      designId: selectedDesign && selectedDesign.id,
      buyer: { name: personalise.buyerName, email: personalise.buyerEmail },
      recipient: isGift ? { name: personalise.recipientName, email: personalise.recipientEmail } : null,
      message: isGift ? personalise.message : '',
      fromName: isGift ? personalise.fromName : '',
      deliverAt: isGift && personalise.deliverWhen === 'scheduled' ? personalise.deliverDate : null,
      deliveryMethods: [personalise.deliverEmail && 'email', personalise.deliverPdf && 'pdf'].filter(Boolean),
      successPath: `${window.location.origin}${base}/gift-cards/success`,
    };
    setError(null);
    setPaying(true);
    try {
      const resp = await giftCardService.purchaseGiftCard(order);
      const data = (resp && resp.data) || resp || {};
      const url = data.paymentUrl || data.url || (data.responseData && data.responseData.url);
      if (url) { window.location.assign(url); return; }
      setError('Online gift-card payment is not available yet. Please try again shortly.');
      setPaying(false);
    } catch (e) {
      setError('Something went wrong starting payment. Please try again.');
      setPaying(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'product':
        return <StepProduct vouchers={vouchers} selectedVoucherId={selectedVoucherId} currency={currency} onChange={handleSelectVoucher} />;
      case 'amount':
        return <StepAmount voucher={voucher} amount={amount} currency={currency} onChange={setAmount} />;
      case 'design':
        return <StepDesign designs={voucherDesigns} selectedDesignId={selectedDesign ? selectedDesign.id : null} amount={amount} currency={currency} salonName={salonName} onChange={setSelectedDesignId} />;
      case 'details':
        return (<><StepPersonalise value={personalise} onChange={updp} /><StepBuyer value={personalise} onChange={updp} locked={loggedIn && isEmail(personalise.buyerEmail)} /></>);
      case 'pay':
        return <StepSummary voucher={voucher} amount={amount} design={selectedDesign} personalise={personalise} currency={currency} hideButton />;
      default:
        return null;
    }
  };

  const overlayClass = `gcs-modal-overlay ${closing ? 'closing' : ''}`;
  const modalClass = `gcs-modal ${closing ? 'closing' : ''}`;

  // Drive the preview pane's height via a CSS variable on the body, so the
  // frosted overlay pane and the scroll area's top padding stay in lockstep (the
  // content scrolls up *behind* the frosted pane — Apple-style — instead of
  // hard-clipping at a solid edge). PREVIEW_PAD_V/PREVIEW_PAD_B mirror the CSS.
  const PREVIEW_PAD_V = 16;
  const PREVIEW_PAD_B = 24;
  const previewPaneHeight = previewH != null
    ? (previewCollapsed ? Math.round(previewH * 0.5) : previewH) + PREVIEW_PAD_V + PREVIEW_PAD_B
    : null;
  const bodyStyle = previewPaneHeight != null ? { '--gcs-preview-h': `${previewPaneHeight}px` } : undefined;

  return (
    <>
    <div className={overlayClass} onClick={handleClose}>
      <div className={modalClass} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Buy a gift card">
        <header className="gcs-modal-header">
          <button type="button" className="gcs-modal-nav" onClick={goBack} aria-label={stepIndex > 0 ? 'Back' : 'Close'}>
            <BackIcon />
          </button>
          <h2 className="gcs-modal-title">Gift cards</h2>
          <button type="button" className="gcs-modal-nav" onClick={handleClose} aria-label="Close"><CloseIcon /></button>
          {/* Step progress on the header's bottom line — a grey track aligned
              to the content column with a charcoal fill that advances per step.
              No separate count text (the step is named by the pane heading). */}
          {!loading && vouchers.length > 0 && (
            <div className="gcs-header-track" aria-hidden={false}>
              <span
                className="gcs-header-progress"
                style={{ width: `${((stepIndex + 1) / steps.length) * 100}%` }}
                role="progressbar"
                aria-valuenow={stepIndex + 1}
                aria-valuemin={1}
                aria-valuemax={steps.length}
                aria-label={`Step ${stepIndex + 1} of ${steps.length}`}
              />
            </div>
          )}
        </header>

        {loading ? (
          <div className="gcs-modal-loading"><div className="gcs-spinner" aria-hidden /></div>
        ) : !vouchers.length ? (
          <div className="gcs-modal-empty">
            <p>{salonName} is not selling gift cards online right now.</p>
          </div>
        ) : (
          <>
            <div className="gcs-modal-body" style={bodyStyle}>
              <div
                ref={previewBoxRef}
                className={`gcs-modal-preview ${previewCollapsed ? 'is-collapsed' : ''}`}
              >
                <GiftCardPreview
                  design={selectedDesign}
                  amount={amount}
                  currency={currency}
                  salonName={salonName}
                  recipientName={isGift ? personalise.recipientName : ''}
                  message={isGift ? personalise.message : ''}
                  fromName={isGift ? personalise.fromName : ''}
                />
              </div>
              <div className="gcs-modal-step-area" ref={stepAreaRef} onScroll={handleStepScroll}>
                <div key={stepIndex} className={`gcs-step-anim ${dir > 0 ? 'from-right' : 'from-left'}`}>
                  <h3 className="gcs-step-heading">{STEP_HEADING[currentStep]}</h3>
                  {renderStep()}
                </div>
              </div>
            </div>

            <div className="gcs-modal-footer">
              {error && <p className="gcs-error">{error}</p>}
              {currentStep === 'pay' ? (
                <button type="button" className="gcs-cta" disabled={!detailsValid || paying} onClick={handlePay}>
                  {paying ? 'Redirecting to payment…' : `Pay ${currency}${payFor(voucher, amount)}`}
                </button>
              ) : (
                <button type="button" className="gcs-cta" disabled={!stepValid} onClick={goNext}>
                  Continue
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>

    <AuthPromptModal
      isOpen={showAuthPrompt}
      onClose={() => setShowAuthPrompt(false)}
      context="giftcard"
    />
    </>
  );
};

export default GiftCardShopModal;
